<template>
  <Card class="container">
    <ConfirmDialog ref="confirm" />
    <div class="cardTitle">{{ $t('Позиции ТРУ') }}</div>
    <div v-if="procedure.partOfVolume" class="card-extra">{{ $t(hintTextUnitPrice) }}</div>
    <DataTableVirtual
      :headers="headers"
      :items="positions"
      disable-sort
      fixed-header
      style="max-height: 580px; overflow-y: auto;"
      class="no-table-border"
    >
      <template v-slot:item.name="{ item }">
        <v-textarea
          auto-grow
          variant="plain"
          readonly
          :model-value="item.name"
          :rows="1"
          :max-rows="3"
        />
      </template>
      <template v-slot:item.quantity="{ item, index }">
        <div class="d-flex align-center justify-space-evenly ga-1" style="width: 100%; height: 100%;">
          <CountInput
            v-if="procedure.partOfVolume"
            v-model="item.quantity"
            :precision="{min: 0, max: 2}"
            :max="initialPositions[index].maxQuantity"
            @change="$emit('changed')"
          />
          <div class="quantity-round" v-else>
            {{ `${item.quantity} ${item.measure}` }}
          </div>
        </div>
      </template>
      <template v-slot:item.price="{ item }">
        <MoneyInput
            label="0,00"
            variant="solo"
            v-model="item.price"
            class="position-grid-fields"
            :rules="[].concat(
                validation.number(null, 999_999_999_999.99, false, true, false,
                'Максимальная цена {_1}')
                )"
            hide-details="auto"
            density="compact"
            single-line
        />
      </template>
      <template v-slot:item.sum="{ item }">
        <span class="price">{{ formatPrice(item.quantity * item.price) }} $</span>
      </template>
      <template v-slot:item.description="{ item }">
        <v-dialog v-if="!!item.description" max-width="500">
          <template v-slot:activator="{ props: activatorProps }">
            <div class="d-flex align-center justify-center" style="width: 100%; height: 100%;">
              <v-icon style="margin: 0px 10px" size="24" v-bind="activatorProps">
                custom:invoice
              </v-icon>
            </div>
          </template>

          <template v-slot:default="{ isActive }">
            <v-card :title="$t('Описание')">
              <template v-slot:append>
                <v-icon icon="mdi-close" @click="isActive.value = false"/>
              </template>
              <v-card-text>
                {{ item.description }}
              </v-card-text>
            </v-card>
          </template>
        </v-dialog>
      </template>
    </DataTableVirtual>
    <div class="d-flex align-start">
      <v-btn
          color="primary"
          density="compact"
          variant="outlined"
          class="ml-4 rounded"
          size="small"
          @click="clearPositions"
      >{{ $t('Очистить') }}</v-btn>
      <v-dialog width="unset">
        <template v-slot:activator="{ props: activatorProps }">
          <v-btn
              color="primary"
              density="compact"
              variant="outlined"
              class="ml-4 rounded"
              size="small"
              v-bind="activatorProps"
          >{{ $t('Загрузить из Excel') }}</v-btn>
        </template>
        <template v-slot:default="{ isActive }">
          <FileInput
              style="width: 80%; align-self: center"
              title="Добавление из Excel"
              subtitle="Данные загружаются только через шаблон, сгенерированный системой. Использование других шаблонов будет игнорировано системой"
              is-edit
              class="mb-3"
              :documents="documents"
              :patterns="patterns"
              :upload-file="(file) => onUpload(file) && (isActive.value = false)"
              :downloadPattern="excelPositionsPattern"
              :uploadExtensions="availableDownloadTypes"
          />
        </template>
      </v-dialog>
      <div class="price-offer-label mr-6 d-flex justify-end flex-column">
        <span class="no-nds-bold">{{ $t('Ценовое предложение: без НДС') }}</span>
        <span>{{ $t('c НДС') }}</span>
      </div>
      <div class="price-offer-sum d-flex justify-end flex-column">
        <span class="no-nds-bold">{{ valid ? formatPrice(price) + '$' : '-' }}</span>
        <span>{{ valid ? formatPrice(priceNds) + '$' : '-' }}</span>
      </div>
    </div>

  </Card>
</template>

<script>
import Card from "@intec/vue-components/form/Card.vue";
import MoneyInput from "@/components/form/MoneyInput.vue";
import CountInput from "@/components/form/CountInput.vue";
import validation from "@/components/validation";
import {formatPrice} from "@/filters";
import ConfirmDialog from "@intec/vue-components/form/ConfirmDialog.vue";
import DataTableVirtual from "@intec/vue-components/table/DataTableVirtual.vue"

import api from "@/api";
import FileInput from "@intec/vue-components/form/FileInput.vue";
import { toast } from '@/lib/toast';

export default {
  components: { FileInput, ConfirmDialog, MoneyInput, CountInput, Card, DataTableVirtual },
  name: 'PositionsView',
  props: {
    positions: Array,
    procedure: Object,
    ndsRate: Number,
    price: {
      type: Object,
      required: true,
    },
    priceNds: {
      type: Object,
      required: true,
    },
    valid: {
      type: Boolean,
      default: true,
    }
  },
  data() {
    return {
      documents: [],
      hintTextUnitPrice: 'Процедура проводится с возможностью подачи заявок на часть объема позиций',
      initialPositions: JSON.parse(JSON.stringify(this.positions)),
      editStates: {},
      validation,
      formatPrice,
      availableDownloadTypes: [
        'application/vnd.ms-excel',
        'application/excel',
        'application/x-excel',
        'application/x-msexcel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ],
      maxFileMBSize: 20
    }
  },
  methods: {
    async clearPositions() {
      const approve = await this.$refs.confirm.open(
          'Очистить позиции ТРУ',
          'Вы действительно хотите удалить все цены?',
      );

      if (approve) {
        this.positions.forEach((element, index) => {
          element.price = null;
          element.quantity = this.initialPositions[index].quantity;
        });
      }
    },
    async onUpload(file) {
      const data = new FormData();
      data.append('file', file);
      data.append('procedureId', this.procedure.id);
      try {
        if (file.size > this.maxFileBytesSize)
          throw new Error(this.$t(`Ошибка: Файл: {_1}. Превышает максимальный размер {_2} Мбайт.`, {'_1': file.name, '_2': this.maxFileMBSize}));
        let resp = await api.post(`/applications/import-positions`, data);

        this.positions.forEach((element) => {
          if (resp.positions[element.procedureItemId].price) {
            element.price = resp.positions[element.procedureItemId].price;
          }

          if (resp.positions[element.procedureItemId].quantity) {
            element.quantity = resp.positions[element.procedureItemId].quantity;
          }
        });
      } catch (error) {
        toast.error(error.message);
      }
    },
    excelPositionsPattern(pattern) {
      window.location.href = pattern.href;
    },
  },
  computed: {
    patterns() {
      return [{
        displayName: 'Скачать шаблон для заполнения',
        fileName: this.$t(`Шаблон загрузки заявки по процедуре №{_1}.xlsx`, {'_1': this.procedure.registryNumber}),
        href: `/procedures/${this.procedure.id}/loadExcelPattern`
      }];
    },
    headers() {
      return [
        {
          title: 'Наименование',
          key: 'name',
          width: '35%'
        },
        {
          title: 'Кол-во',
          key: 'quantity',
          width: '15%'
        },
        {
          title: 'Цена за ед. (без НДС)',
          key: 'price',
          width: '30%'
        },
        {
          title: 'Сумма (без НДС)',
          key: 'sum',
          width: '20%'
        },
        {
          title: '',
          key: 'description',
          width: '5%'
        }
      ];
    },
  },
}
</script>

<style lang="scss" scoped>
:deep(.v-data-table) {
  th {
    background-color: #F6F6F6 !important;
    .v-data-table-header__content {
      font-size: 16px;
      color: #000000 !important;
    }
  }
  .v-table__wrapper > table > tbody > tr > td {
    padding: 0 5px;
  }
}

:deep(.v-input__details) {
  min-height: 10px;
  padding: 2px 0 0;

  .v-messages > .v-messages__message {
    font-size: 12px;
    line-height: 12px;
    margin-bottom: 2px;
  }
}

:deep(.v-field__input) {
  font-size: 1rem;
}

.no-table-border {
  border: none;
  border-radius: 0;
}

.quantity-round {
  border-radius: 8px;
  background-color: #C8CAD1;
  padding: 5px;
  display: flex;
  justify-content: center;
  width: inherit;
}

.position-grid-fields {
  margin: 5px;
}

.price {
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
}

.card-extra {
  font-size: 14px;
  color: #949494;
  margin-bottom: 12px;
}

.no-nds-bold {
  font-weight: bold;
}

.price-offer-label {
  text-align: right;
  margin-left: auto;
}
</style>
